import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Typography,
  Box,
  alpha,
  Divider,
  Paper,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import { useTranslation } from 'next-i18next';

import GetStartedBtn from 'views/RentMyMotorcycleView/components/GetStartedBtn';

const PREFIX = 'GotYourBack';

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  title: `${PREFIX}-title`,
  partContainer: `${PREFIX}-partContainer`,
  partIcn: `${PREFIX}-partIcn`,
  partTitle: `${PREFIX}-partTitle`,
  partDesc: `${PREFIX}-partDesc`,
  getStartedBox: `${PREFIX}-getStartedBox`,
  divider: `${PREFIX}-divider`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    marginTop: theme.spacing(13),
    marginBottom: theme.spacing(13),
    maxWidth: theme.spacing(166.25),
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(22),
      marginTop: theme.spacing(18),
    },
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
      marginBottom: theme.spacing(5.5),
    },
  },

  [`& .${classes.title}`]: {
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(5),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(36),
      marginBottom: theme.spacing(8),
      textAlign: 'left',
    },
    [theme.breakpoints.up('lg')]: {
      textAlign: 'center',
      marginBottom: theme.spacing(11.25),
    },
  },

  [`& .${classes.partContainer}`]: {
    padding: theme.spacing(0, 0, 4.5),
    [theme.breakpoints.only('sm')]: {
      textAlign: 'center',
    },
    [theme.breakpoints.only('md')]: {
      marginBottom: theme.spacing(6),
    },
    [theme.breakpoints.up('lg')]: {
      height: '100%',
      borderRadius: 30,
      border: `1px solid ${theme.palette.grey[200]}`,
      padding: theme.spacing(7.75, 3.5),
    },
  },

  [`& .${classes.partIcn}`]: {
    width: theme.spacing(7.5),
    height: theme.spacing(7.5),
    borderRadius: 15,
    boxShadow: `0 0 4px 0 ${alpha(theme.palette.common.black, 0.15)}`,
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    marginBottom: theme.spacing(5.25),
    [theme.breakpoints.only('sm')]: {
      width: theme.spacing(4.5),
      height: theme.spacing(4.5),
      borderRadius: 5,
      margin: 'auto',
      marginBottom: theme.spacing(2),
    },
    '& .MuiSvgIcon-root': {
      width: theme.spacing(4.5),
      height: theme.spacing(4.5),
      color: theme.palette.common.fogWhite,
      [theme.breakpoints.only('sm')]: {
        width: theme.spacing(2.75),
        height: theme.spacing(2.75),
      },
    },
  },

  [`& .${classes.partTitle}`]: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: '500',
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.pxToRem(16),
    },
  },

  [`& .${classes.partDesc}`]: {
    letterSpacing: theme.typography.pxToRem(0.5),
    lineHeight: theme.typography.pxToRem(24),
    marginTop: theme.spacing(1.5),
    fontSize: theme.typography.pxToRem(14),
    [theme.breakpoints.only('md')]: {
      width: '85%',
    },
  },

  [`& .${classes.getStartedBox}`]: {
    marginTop: theme.spacing(9),
    textAlign: 'center',
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(4.5),
    },
  },

  [`& .${classes.divider}`]: {
    marginTop: theme.spacing(10),
    backgroundColor: theme.palette.grey[200],
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
    },
  },
}));

const GotYourBack = () => {
  const { t } = useTranslation('fe_er_rent_my_motorcycle');

  const partsIcn = [
    <SecurityIcon />,
    <VerifiedUserIcon />,
    <HeadsetMicIcon />,
    <StarHalfIcon />,
  ];

  return (
    <Root>
      <Container maxWidth="xl" classes={{ root: classes.mainContainer }}>
        <Typography variant="h2" classes={{ root: classes.title }}>
          {t('fe_er_rent_my_motorcycle:got_your_back_title')}
        </Typography>

        <Grid container alignItems="stretch" spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <Box className={classes.partContainer}>
              <Box className={classes.partIcn}>{partsIcn[0]}</Box>
              <Typography variant="h4" classes={{ root: classes.partTitle }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.title1')}
              </Typography>
              <Typography classes={{ root: classes.partDesc }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.desc1')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Box className={classes.partContainer}>
              <Box className={classes.partIcn}>{partsIcn[1]}</Box>
              <Typography variant="h4" classes={{ root: classes.partTitle }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.title2')}
              </Typography>
              <Typography classes={{ root: classes.partDesc }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.desc2')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Box className={classes.partContainer}>
              <Box className={classes.partIcn}>{partsIcn[2]}</Box>
              <Typography variant="h4" classes={{ root: classes.partTitle }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.title3')}
              </Typography>
              <Typography classes={{ root: classes.partDesc }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.desc3')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Box className={classes.partContainer}>
              <Box className={classes.partIcn}>{partsIcn[3]}</Box>
              <Typography variant="h4" classes={{ root: classes.partTitle }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.title4')}
              </Typography>
              <Typography classes={{ root: classes.partDesc }}>
                {t('fe_er_rent_my_motorcycle:got_your_back_parts.desc4')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Box className={classes.getStartedBox}>
          <GetStartedBtn />
        </Box>

        <Paper sx={{ display: { md: 'block', lg: 'none' } }}>
          <Divider classes={{ root: classes.divider }} />
        </Paper>
      </Container>
    </Root>
  );
};

export default GotYourBack;
